import { makeStyles, Grid, Typography, TextField } from '@material-ui/core';
import React, { useState, useEffect } from 'react';
import {
  ReferenceInput,
  required,
  SelectInput,
  TextInput,
  RadioButtonGroupInput
} from 'react-admin';
import TenantConfigurationApi from '../../core/apis/TenantConfigurationApi';
import FundApi from '../../core/apis/FundApi';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';
import { useForm, Field } from 'react-final-form';

import DateInput from '../../core/components/forked/input/DateInput';
import {
  fieldLengthValidator,
  isValidDate,
  formatDate
} from '../../core/utils/index';
import { fundTypes, fundStatus } from './FundLookups';
import {
  emailFormatter,
  emailParser,
  validateFundIdRa,
  validateSingleEmailFromArray,
  validateTargetCommitment,
  validateTargetNbOfInvestors
} from './creationSteps/utils';
import FundTagInput from './FundTagInput';
import SelectMainFundModal from '../../features/funds/SelectMainModal';
import { INVESTMENT_STRUCTURE_OPTIONS_RA } from '../../features/funds/InvestmentStructureUtil';
import RequiredLabel from './RequiredLabel';
import MainFundSection from './MainFundSection';
import CheckBoxField from '../../core/components/CheckBoxField';
import { sharedFundStyles } from './fundStyles';

const useStyles = makeStyles({
  ...sharedFundStyles,
  headerContainer: { paddingTop: '0px !important' },
  formCheckBox: { marginTop: '26px' },
  formLink: { marginTop: '18px' },
  addLabel: { color: '#2962FF', marginTop: '14px' },
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(200px, 500px))',
    columnGap: '24px',
    rowGap: '4px',
    alignItems: 'start',
    '& .MuiFormControl-marginDense': {
      marginTop: 0,
      marginBottom: 0
    },
    '& label .MuiFormLabel-asterisk': {
      color: '#f44336 !important'
    },
    '& label .MuiInputLabel-asterisk': {
      color: '#f44336 !important'
    }
  }
});

const useFundSelfServe = notifyServerError => {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    TenantConfigurationApi.get('Fund Self-Serve')
      .then(response => setEnabled(response?.pageData[0]?.value === '1'))
      .catch(notifyServerError);
  }, [notifyServerError]);
  return enabled;
};

const FundSummary = props => {
  const form = useForm();
  const classes = useStyles();
  const notifyServerError = useNotifyServerError();

  const fundSelfServeEnabled = useFundSelfServe(notifyServerError);

  const [mainFundModalOpen, setMainFundModalOpen] = useState(false);
  const [selectedMainFund, setSelectedMainFund] = useState(null);

  const handleSelectMainFund = fund => {
    setSelectedMainFund(fund);
    form.change('mainFundId', fund.id);
    form.change('mainFundName', fund.name);
  };

  const { values } = form.getState();
  const isCoInvestment = values.investmentStructure === 'Co-Investment';
  const showMainFundError = isCoInvestment && !values.mainFundId;

  useEffect(() => {
    if (!values.mainFundId) {
      setSelectedMainFund(null);
      return;
    }
    const fetchMainFund = async () => {
      try {
        const response = await FundApi.getFundsByIds([values.mainFundId]);
        setSelectedMainFund(response?.pageData?.[0] || null);
      } catch (error) {
        notifyServerError(error);
      }
    };
    fetchMainFund();
  }, [values.mainFundId, notifyServerError]);

  const requiredIfSelfServe = () => (fundSelfServeEnabled ? [required()] : []);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} className={classes.headerContainer}>
          <Typography variant="h5">Fund Details</Typography>
        </Grid>
      </Grid>

      <div className={classes.gridContainer}>
        {/* Row 1 */}
        <div className={classes.formGroup}>
          <TextInput
            source="name"
            label={<RequiredLabel label="Fund Name" />}
            validate={[required(), fieldLengthValidator(256)]}
            options={{ autoComplete: 'off' }}
            variant="standard"
          />
        </div>
        <div className={classes.formGroup}>
          <DateInput
            source="inceptionDate"
            label={<RequiredLabel label="Fund Inception Date" />}
            validate={[required(), isValidDate]}
            placeholder="MM/dd/yyyy"
            customParseValue={formatDate}
          />
        </div>
        <div className={classes.dataRoomGroup}>
          <RadioButtonGroupInput
            source="dataRoom"
            label={
              fundSelfServeEnabled ? (
                <RequiredLabel label="Data Room" />
              ) : (
                'Data Room'
              )
            }
            margin="none"
            classes={{ label: classes.label }}
            options={{ row: true }}
            choices={[
              { id: true, name: 'Yes' },
              { id: false, name: 'No' }
            ]}
            validate={fundSelfServeEnabled ? [required()] : []}
            optionText="name"
            optionValue="id"
          />
        </div>

        {/* Row 2 */}
        <div className={classes.formGroup}>
          <TextInput
            source="id"
            label={<RequiredLabel label="Fund ID" />}
            disabled={!props.creation}
            validate={[required(), fieldLengthValidator(20), validateFundIdRa]}
            options={{ autoComplete: 'off' }}
            variant="standard"
          />
        </div>
        <div className={classes.formGroup}>
          <DateInput
            source="startDate"
            label="Start Date"
            validate={[isValidDate]}
            placeholder="MM/dd/yyyy"
            customParseValue={formatDate}
          />
        </div>
        <div className={classes.formGroup}>
          <SelectInput
            source="investmentStructure"
            label={<RequiredLabel label="Investment Structure" />}
            choices={INVESTMENT_STRUCTURE_OPTIONS_RA}
            validate={[required()]}
            optionText="name"
            optionValue="id"
            variant="standard"
          />
        </div>

        {/* Row 3 */}
        <div className={classes.formGroup}>
          <TextInput
            source="aka"
            label="A.K.A"
            options={{ autoComplete: 'off' }}
            variant="standard"
            validate={fieldLengthValidator(255)}
          />
        </div>
        <div className={classes.formGroup}>
          <DateInput
            source="endDate"
            label="Closing Date/As of Date"
            validate={[isValidDate]}
            placeholder="MM/dd/yyyy"
            customParseValue={formatDate}
          />
        </div>
        <div className={classes.mainFundGroup}>
          <Field name="mainFundId">{() => null}</Field>
          <MainFundSection
            isCoInvestment={isCoInvestment}
            showMainFundError={showMainFundError}
            hasSelectedFund={Boolean(values.mainFundId)}
            classes={classes}
            onClear={() => {
              form.change('mainFundId', null);
              form.change('mainFundName', null);
              setSelectedMainFund(null);
            }}
            onSelect={() => setMainFundModalOpen(true)}
            renderSelectedFund={isCo => (
              <TextField
                label={`Main Fund${isCo ? ' *' : ''}`}
                value={selectedMainFund?.name || ''}
                variant="standard"
                fullWidth
                InputProps={{ readOnly: true }}
                disabled
              />
            )}
          />
        </div>

        {/* Row 4 */}
        <div className={classes.formGroup}>
          <SelectInput
            source="fundType"
            label={
              fundSelfServeEnabled ? (
                <RequiredLabel label="Fund Type" />
              ) : (
                'Fund Type'
              )
            }
            choices={fundTypes}
            validate={requiredIfSelfServe()}
            variant="standard"
          />
        </div>
        <div className={classes.formGroup}>
          <ReferenceInput
            label={<RequiredLabel label="Currency" />}
            source="currency"
            reference="currencies"
            variant="standard"
          >
            <SelectInput optionText="code" validate={[required()]} />
          </ReferenceInput>
        </div>
        <div className={classes.formGroup}>
          <SelectInput
            source="status"
            label={<RequiredLabel label="Publishing Status" />}
            choices={fundStatus}
            validate={[required()]}
            variant="standard"
          />
        </div>

        {/* Row 5 */}
        <div className={classes.formGroup}>
          <ReferenceInput
            label={<RequiredLabel label="Asset Class" />}
            source="classCode"
            reference="fundclasses"
            variant="standard"
          >
            <SelectInput optionText="description" validate={[required()]} />
          </ReferenceInput>
        </div>
        <div className={classes.formGroup}>
          <ReferenceInput
            source="currencyAlternate"
            resource=""
            reference="currencies"
            label="Alternative Currency"
            variant="standard"
          >
            <SelectInput optionText="code" />
          </ReferenceInput>
        </div>
        {!props.creation ? (
          <div className={classes.formGroup}>
            <FundTagInput
              hasEditTagPermission={props.hasEditFundTagPermission}
            />
          </div>
        ) : fundSelfServeEnabled ? (
          <div className={classes.formGroup}>
            <Field
              name="acknowledge"
              component={CheckBoxField}
              label={
                <span>
                  I acknowledge that the creation of this fund may be subject to
                  an additional fee that will be communicated via an order form.
                  <span style={{ color: 'red' }}>{' *'}</span>
                </span>
              }
              type="checkbox"
              parse={value => {
                return !!value;
              }}
              defaultValue={false}
              validate={value =>
                fundSelfServeEnabled && value !== true
                  ? 'You must acknowledge before proceeding.'
                  : undefined
              }
            />
          </div>
        ) : (
          <div />
        )}

        {/* Row 6 */}
        <div className={classes.formGroup}>
          <ReferenceInput
            label="Fund Family"
            source="fundFamilyId"
            reference="fundfamilies"
            filter={{ orphanVisibility: 'ALL_VISIBLE' }}
            perPage={-1}
            variant="standard"
            disabled={props.disableFundFamily}
          >
            <SelectInput optionText="name" />
          </ReferenceInput>
        </div>
        <div className={classes.formGroup}>
          <TextInput
            source="targetCommitment"
            label={
              fundSelfServeEnabled ? (
                <RequiredLabel label="Target Commitment" />
              ) : (
                'Target Commitment'
              )
            }
            validate={[
              ...(fundSelfServeEnabled ? [required()] : []),
              validateTargetCommitment
            ]}
            variant="standard"
          />
        </div>
        <div />

        {/* Row 7 */}
        <div className={classes.formGroup}>
          <TextInput
            source="emailAddresses"
            label="Fund Contact Email"
            format={emailFormatter}
            parse={emailParser}
            validate={[validateSingleEmailFromArray]}
            allowEmpty
            options={{ autoComplete: 'off' }}
            variant="standard"
          />
        </div>
        <div className={classes.formGroup}>
          <TextInput
            source="targetNbOfInvestors"
            label={
              fundSelfServeEnabled ? (
                <RequiredLabel label="Target Number of Investors" />
              ) : (
                'Target Number of Investors'
              )
            }
            validate={[
              ...(fundSelfServeEnabled ? [required()] : []),
              validateTargetNbOfInvestors
            ]}
            variant="standard"
          />
        </div>
        <div />
      </div>

      <SelectMainFundModal
        open={mainFundModalOpen}
        onClose={() => setMainFundModalOpen(false)}
        onSelect={handleSelectMainFund}
        fundId={form.getState().values.id}
      />
    </>
  );
};

export default FundSummary;
